import React, { useState } from 'react';

import {
  DollarOutlined,
  ThunderboltOutlined,
  CreditCardOutlined,
  EditOutlined,
  WalletOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Tag, Tooltip as TooltipAntD } from 'antd';

// antd 4.10 typings don't expose `children` on Tooltip; cast to any
// following the project-wide pattern (see FinancialTable: Drawer, Modal).
const Tooltip: any = TooltipAntD;
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { T } from 'react-translator-component';

import { OpenDrawerContainer } from './styled';

import { ReactComponent as Cop } from '../../../assets/peso-colombiano.svg';
import { ReactComponent as PixIcon } from '../../../assets/pix.svg';
import ticketLogLogo from '../../../assets/ticket-log-logo.png';
import {
  getSummary,
  getTransactions,
  downloadTransactions,
  getTenants,
  getChargerboxes,
} from '../../../hooks/financial';
import { RECONCILIATION_STATUS_META } from '../../../hooks/reconciliation';
import { hasReconciliationRole } from '../../../utils/hasReconciliationRole/hasReconciliationRole';
import { PaymentTypes } from '../../../types/responses/Transactions';
import { isTransferSettled } from '../../../utils/paymentSituation/paymentSituation';
import {
  PaymentStatus,
  PaymentStatusColor,
  PaymentStatusText,
} from '../../../utils/paymentStatus';
import { resolveNfeUrls } from '../../../utils/resolveNfeUrls/resolveNfeUrls';
import { ConfirmationNumber } from '../../atoms/icons/ConfirmationNumber';
import { TenantLogoIcon } from '../../atoms/icons/TenantLogoIcon';
import { IconAreaCenter } from '../TransactionsTable/styled';

export enum FinancialInfoType {
  'TRANSACTION' = 'Recarga',
  'USER' = 'Depósito',
  'IDLE' = 'Ociosidade',
}

export enum TabType {
  'ALLMOVEMENTS' = 'allMovements',
  'RECHARGES' = 'recharges',
  'DEPOSITS' = 'deposits',
}

export {
  PaymentStatus,
  PaymentStatusColor,
  PaymentStatusText,
} from '../../../utils/paymentStatus';

export interface DataType {
  key: number;
  userId: number;
  type: FinancialInfoType;
  customer: string;
  tenant: string;
  alias: string;
  nfeStatus: string;
  value: number;
  chargeBox: string;
  paymentType: string;
  paymentTypeString: PaymentTypes;
  isSelected: boolean;
  paymentStatus: string;
  statusBtoB?: string;
  effectiveStatus?: string | null;
  autoStatus?: string | null;
  manualReconciledAt?: string | null;
  manualReason?: string | null;
  manualReconciledByUserPk?: number | null;
  paymentDateTime: string;
  paymentTransactionAmountHumanReadable: string;
  externalPaymentReference: string;
  targetType: FinancialInfoType;
  paidTimestamp: string;
  energyCostHumanReadable: string;
  taxIncomeHumanReadable: string;
  moneyVoucherHumanReadable: string;
  moneyCoin: string;
  moneyPerTransactionIncomeHumanReadable: string;
  paymentTransactionAmountRefunded: number;
  paymentTransactionAmountAvailableHumanReadable: string;
  total: string;
  nfeUrls: string[];
  energyHumanReadable: string;
  moneyPerKilowattIncome: number;
  splits: {
    value: number;
    receiver_account_uuid: string;
    receiver_account_name: string;
    amount: number;
  }[];
}

export interface TransactionDTO {
  transactionPk: number;
  paidTimestamp: string;
  paymentTransactionAmount: number;
  paymentTransactionAmountHumanReadable: number;
  paymentExternalId: string;
  userId: number;
  userFirstName: string;
  paymentType: PaymentTypes;
  paymentTypeString: PaymentTypes;
  userLastName: string | null;
  paymentStatus: string;
  statusBtoB?: string;
  voucherUnitPk?: number;
  targetType: FinancialInfoType;
  tenantName: string;
  tenantAlias: string;
  chargeBoxDescription: string;
  nfeStatus: string;
  energyCostHumanReadable: string;
  taxIncomeHumanReadable: string;
  moneyVoucherHumanReadable: string;
  moneyCoin: string;
  moneyPerTransactionIncomeHumanReadable: string;
  startTimestamp: string;
  stopTimestamp: string;
  idTag: string;
  paymentTransactionAmountRefunded: number;
  paymentTransactionAmountAvailableHumanReadable: string;
  energyHumanReadable: string;
  moneyPerKilowattIncome: number;
  nfeUrls: string[];
  nfeDanfseUrls: string[];
  splits: {
    value: number;
    receiver_account_uuid: string;
    receiver_account_name: string;
    amount: number;
  }[];
  effectiveStatus?: string | null;
  autoStatus?: string | null;
  manualReconciledAt?: string | null;
  manualReason?: string | null;
  manualReconciledByUserPk?: number | null;
}

interface OpenDrawerProps {
  visible: boolean;
  data: DataType | null;
}

const rawMaskMoney = (value: string) => {
  const onlyDigits = value
    .split('')
    .filter((s) => /\d/.test(s))
    .join('')
    .padStart(3, '0');

  const digitsFloat = onlyDigits.slice(0, -2) + '.' + onlyDigits.slice(-2);

  return digitsFloat;
};

const maskCurrency = (valor, locale = 'pt-BR', currency = 'BRL') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(valor);
};

export const mascaraMoeda = (value: any) => {
  const digitsFloat = rawMaskMoney(value);

  return maskCurrency(digitsFloat);
};

export const formatManualAdjustmentValue = (value?: number) => {
  if (value == null) {
    return null;
  }
  return `${value < 0 ? '-' : '+'} ${mascaraMoeda(value.toFixed(2))}`;
};

export const isManualAdjustmentRow = (data?: DataType | null) =>
  data?.paymentTypeString === PaymentTypes.MANUAL_DASHBOARD &&
  data?.targetType === FinancialInfoType.USER;

export const useFinancialTable = () => {
  const [openDrawerProps, setOpenDrawerProps] = useState<OpenDrawerProps>({
    visible: false,
    data: null,
  });

  const [financialInfoTransactions, setFinancialInfoTransactions] =
    useState<any>({
      error: false,
      data: null,
      loading: false,
      request: tryToGetTransactions,
      pagination: { page: 1, pageSize: 10 },
      filters: { startDate: '', endDate: '', transactionPk: '' },
    });

  const [
    financialInfoTransactionsSummary,
    setFinancialInfoTransactionsSummary,
  ] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: tryToGetFinancialInfoSummary,
    pagination: { page: 1, pageSize: 10 },
    filters: { startDate: '', endDate: '', transactionPk: '' },
  });

  const [tenants, setTenants] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: tryToGetTenants,
  });

  const [chargeboxes, setChargeboxes] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: tryToGetChargeboxes,
  });

  async function tryToGetTransactions(
    targetType: 'allMovements' | 'deposits' | 'recharges',
    page: number = 1,
    pageSize: number = 10,
    startDate: string = moment().startOf('month').format('YYYY-MM-DD'),
    endDate: string = moment().format('YYYY-MM-DD'),
    transactionPk?: string,
    orderBy?: string,
    tenantId?: string,
    chargeBoxId?: string,
    paymentStatus?: string,
  ) {
    try {
      setFinancialInfoTransactions((prevState) => ({
        ...prevState,
        loading: true,
        error: false,
      }));

      const transactionsResponse = await getTransactions(
        targetType,
        page,
        pageSize,
        startDate,
        endDate,
        transactionPk,
        orderBy,
        tenantId,
        chargeBoxId,
        paymentStatus,
      );

      if (transactionsResponse.data) {
        setFinancialInfoTransactions((prevState) => ({
          ...prevState,
          data: transactionsResponse.data.content,
          loading: false,
          error: false,
          pagination: {
            page,
            pageSize,
            total: transactionsResponse.data.totalElements,
          },
        }));
      }
    } catch {
      return setFinancialInfoTransactions((prevState) => ({
        ...prevState,
        error: true,
        loading: false,
        data: null,
      }));
    }
  }

  async function tryToGetTenants() {
    const { data } = await getTenants();

    setTenants({ ...tenants, data });
  }

  async function tryToGetChargeboxes() {
    const { data } = await getChargerboxes();

    setChargeboxes({ ...tenants, data });
  }

  async function tryToGetFinancialInfoSummary(
    page: number = 1,
    pageSize: number = 10,
    startDate: string = moment().startOf('month').format('YYYY-MM-DD'),
    endDate: string = moment().format('YYYY-MM-DD'),
    transactionPk?: string,
    orderBy?: string,
    tenantId?: string,
    chargeBoxId?: string,
    paymentStatus?: string,
  ) {
    try {
      setFinancialInfoTransactionsSummary((prevState) => ({
        ...prevState,
        loading: true,
        error: false,
      }));

      const transactionsSummaryResponse = await getSummary(
        page,
        pageSize,
        startDate,
        endDate,
        transactionPk,
        orderBy,
        tenantId,
        chargeBoxId,
        paymentStatus,
      );

      if (transactionsSummaryResponse.data) {
        setFinancialInfoTransactionsSummary((prevState) => ({
          ...prevState,
          data: transactionsSummaryResponse.data,
          loading: false,
          error: false,
          pagination: {
            page,
            pageSize,
            total: transactionsSummaryResponse.data.totalElements,
          },
        }));
      }
    } catch {
      return setFinancialInfoTransactionsSummary((prevState) => ({
        ...prevState,
        error: true,
        loading: false,
        data: null,
      }));
    }
  }

  const handleStatus = (
    paymentStatus: string,
    isRefunded = false,
    isManual = false,
    statusBtoB?: string,
  ) => {
    const isPartialRefunded =
      isRefunded && paymentStatus !== PaymentStatus.REFUNDED;

    // MV2-1802: "o repasse ao lojista reprovou?" vem do mesmo lugar que a cor da lista e o filtro do
    // CS usam. Nulo não é repasse pendente — é pagamento sem repasse a fazer, o normal de tenant sem
    // split configurado. Ver utils/paymentSituation.
    //
    // PENDÊNCIA: o endpoint do financeiro ainda não devolve payment_situation, então aqui a decisão
    // continua sendo tomada no front, só que pela função compartilhada.
    const isBtoBNotAccredited = !isTransferSettled(statusBtoB);

    const status = isBtoBNotAccredited
      ? PaymentStatus.PENDING
      : isManual
      ? PaymentStatus.MANUAL_ADJUSTMENT
      : isPartialRefunded
      ? PaymentStatus.PARTIAL_REFUND
      : paymentStatus;

    return (
      <>
        <Tag color={PaymentStatusColor[status]} style={{ marginRight: 0 }}>
          {PaymentStatusText[status]}
        </Tag>
      </>
    );
  };

  function renderConciliacaoCell(
    value: string | null | undefined,
    data: DataType,
  ): React.ReactNode {
    if (!value) return '—';
    const meta = RECONCILIATION_STATUS_META[value];
    const tag = meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>{value}</Tag>;

    // MV2-1909: a ação manual mora no drawer da transação agora. Aqui só exibimos
    // o status; tooltip no modo manual oferece contexto (motivo/data/responsável).
    const isManual = value === 'RECONCILED_MANUAL';
    if (!isManual) return tag;

    const tip = (
      <span>
        {data.manualReason && (
          <>
            <strong>Motivo:</strong> {data.manualReason}
            <br />
          </>
        )}
        {data.manualReconciledAt && (
          <>
            <strong>Em:</strong>{' '}
            {moment(data.manualReconciledAt).format('DD/MM/YYYY HH:mm')}
            <br />
          </>
        )}
        {data.manualReconciledByUserPk != null && (
          <>
            <strong>Por:</strong> user #{data.manualReconciledByUserPk}
          </>
        )}
      </span>
    );
    return (
      <Tooltip title={tip}>{tag}</Tooltip>
    );
  }

  function generateColumns(tabType: TabType): ColumnsType<any> {
    let dynamicColuns: ColumnsType<any> = [];

    if (tabType === TabType.ALLMOVEMENTS) {
      dynamicColuns = [
        {
          title: 'Recarga (ID)',
          dataIndex: 'key',
          width: '10%',
          key: 'key',
        },
        {
          title: 'Tipo',
          dataIndex: 'targetType',
          key: 'targetType',
          width: '10%',
          render(value) {
            return (
              <div className='type-container'>
                {value === FinancialInfoType.TRANSACTION ? (
                  <ThunderboltOutlined
                    style={{ marginRight: 6, marginTop: 2 }}
                  />
                ) : (
                  <DollarOutlined style={{ marginRight: 6, marginTop: 2 }} />
                )}

                {value}
              </div>
            );
          },
        },
        {
          title: 'Data da Recarga',
          dataIndex: 'startDate',
          key: 'startDate',
          width: '10%',
          align: 'center',
        },
        {
          title: 'Data de Pagamento',
          dataIndex: 'paymentPaidDate',
          key: 'paymentPaidDate',
          width: '10%',
          align: 'center',
        },
        {
          title: 'Cliente',
          dataIndex: 'customer',
          key: 'customer',
        },

        {
          title: 'Valor',
          dataIndex: 'paymentTransactionAmountHumanReadable',
          key: 'paymentTransactionAmountHumanReadable',
          width: '10%',
          align: 'center',
          render: (value, data) =>
            isManualAdjustmentRow(data)
              ? formatManualAdjustmentValue(data.value)
              : value,
        },
        {
          title: 'Status',
          dataIndex: 'paymentStatus',
          key: 'paymentStatus',
          align: 'center',
          width: '10%',
          render: (value, data) =>
            handleStatus(
              value,
              Boolean(data.paymentTransactionAmountRefunded),
              isManualAdjustmentRow(data),
              data.statusBtoB,
            ),
        },
        ...(hasReconciliationRole() ? [{
          title: (
            <Tooltip title='Status sincronizado periodicamente. Para a visão mais recente, baixe o relatório pelo modal de conciliação.'>
              <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
                Conciliação
              </span>
            </Tooltip>
          ),
          dataIndex: 'effectiveStatus',
          key: 'effectiveStatus',
          align: 'center' as const,
          width: '10%',
          render: (value: string | null, data: DataType) =>
            renderConciliacaoCell(value, data),
        }] : []),
        {
          title: 'Forma de pagamento',
          dataIndex: 'paymentType',
          key: 'paymentType',
          align: 'center',
          width: '10%',
        },
      ];
    } else {
      dynamicColuns = [
        {
          title:
            tabType === TabType.RECHARGES ? 'Recarga (ID)' : 'Depósito (ID)',
          dataIndex: 'key',
          width: '10%',
          key: 'key',
        },
      ];

      if (tabType === TabType.RECHARGES) {
        dynamicColuns = [
          ...dynamicColuns,
          {
            title: 'Data da Recarga',
            dataIndex: 'startDate',
            key: 'startDate',
            width: '10%',
            align: 'center',
          },
        ];
      }

      if (tabType === TabType.RECHARGES || tabType === TabType.DEPOSITS) {
        dynamicColuns = [
          ...dynamicColuns,
          {
            title: 'Data de Pagamento',
            dataIndex: 'paymentPaidDate',
            key: 'paymentPaidDate',
            width: '10%',
          },
          {
            title: T('Account'),
            dataIndex: 'tenant',
            key: 'tenant',
            render: (text, data) => {
              if (!text) {
                return 'Desconhecido';
              }

              const tenantAlias = data.alias ? data.alias : 'move';

              return (
                <div className='tenant-container'>
                  <TenantLogoIcon
                    size={30}
                    logoIconUrl={text}
                    tenantAlias={tenantAlias}
                  />

                  <td className='tenant-name'>{text}</td>
                </div>
              );
            },
          },
          {
            title: 'Cliente',
            dataIndex: 'customer',
            key: 'customer',
          },
          {
            title: 'Valor',
            dataIndex: 'paymentTransactionAmountAvailableHumanReadable',
            key: 'paymentTransactionAmountAvailableHumanReadable',
            align: 'center',
            render: (value, data) =>
              isManualAdjustmentRow(data)
                ? formatManualAdjustmentValue(data.value)
                : value,
          },
          {
            title: 'Status',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            align: 'center',
            width: '10%',
            render: (value, data) =>
              handleStatus(
                value,
                Boolean(data.paymentTransactionAmountRefunded),
                isManualAdjustmentRow(data),
                data.statusBtoB,
              ),
          },
          {
            title: (
              <Tooltip title='Status sincronizado periodicamente. Para a visão mais recente, baixe o relatório pelo modal de conciliação.'>
                <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>
                  Conciliação
                </span>
              </Tooltip>
            ),
            dataIndex: 'effectiveStatus',
            key: 'effectiveStatus',
            align: 'center',
            width: '10%',
            render: (value: string | null, data: DataType) =>
              renderConciliacaoCell(value, data),
          },
        ];
      }

      if (tabType === TabType.RECHARGES) {
        dynamicColuns.push(
          {
            title: 'Carregador',
            dataIndex: 'chargeBox',
            key: 'chargeBox',
          },
          // {
          //   title: 'NF',
          //   dataIndex: 'nfStatus',
          //   key: 'nfStatus',
          // }
        );
      }
    }

    dynamicColuns.push({
      title: '',
      key: 'action',
      width: '5%',

      render: (_, record) => (
        <OpenDrawerContainer
          onClick={() => setOpenDrawerProps({ visible: true, data: record })}
        >
          <RightOutlined style={{ color: '#757b88' }} />
        </OpenDrawerContainer>
      ),
    });

    return dynamicColuns;
  }

  const iconMappings = {
    [PaymentTypes.MERCADO_PAGO_CARD]: {
      icon: <CreditCardOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
      additionalIcon: (
        <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
      ),
    },
    [PaymentTypes.PAGBANK_CARD]: {
      icon: <CreditCardOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
      additionalIcon: (
        <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
      ),
    },
    [PaymentTypes.MERCADO_PAGO_PIX]: {
      icon: <PixIcon />,
      additionalIcon: (
        <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
      ),
    },
    [PaymentTypes.WALLET]: {
      icon: <WalletOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
      additionalIcon: (
        <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
      ),
    },
    [PaymentTypes.MANUAL_DASHBOARD]: {
      icon: <EditOutlined style={{ fontSize: 16, color: '#6b7280' }} />,
    },
    [PaymentTypes.VOUCHER]: {
      icon: <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />,
    },
    [PaymentTypes.PAGOS_TERPEL]: {
      icon: <Cop />,
    },
    [PaymentTypes.TICKET_LOG]: {
      icon: (
        <img src={ticketLogLogo} alt='Ticket Log Logo' width={37} height={32} />
      ),
      additionalIcon: (
        <ConfirmationNumber fill='#6b7280' style={{ marginLeft: 4 }} />
      ),
    },
  };

  const getTransactionIcon = (
    transactionPaymentType: PaymentTypes,
    voucherUnitPk?: number,
  ) => {
    const mapping = iconMappings[transactionPaymentType];

    if (!mapping) {
      return null;
    }

    const { icon, additionalIcon } = mapping;

    if (voucherUnitPk) {
      return (
        <IconAreaCenter>
          {icon}
          {additionalIcon}
        </IconAreaCenter>
      );
    }

    return icon;
  };

  function generateDataSource(tabType: TabType): DataType[] {
    const dataSource: DataType[] = [];

    const mapFinancialAllMovements = (financial: TransactionDTO) => {
      const handleName = () => {
        if (financial.userFirstName) {
          const lastName = `${
            financial.userLastName ? financial.userLastName : ''
          }`;

          return `${financial.userFirstName} ${lastName}`;
        }

        return financial.idTag || '';
      };

      return {
        targetType: FinancialInfoType[financial.targetType],
        key: financial.transactionPk,
        userId: financial.userId,
        customer: handleName(),
        type: financial.targetType,
        paymentStatus: financial.paymentStatus,
        statusBtoB: financial.statusBtoB,
        effectiveStatus: financial.effectiveStatus,
        autoStatus: financial.autoStatus,
        manualReconciledAt: financial.manualReconciledAt,
        manualReason: financial.manualReason,
        manualReconciledByUserPk: financial.manualReconciledByUserPk,
        paymentType: getTransactionIcon(
          financial.paymentType,
          financial.voucherUnitPk,
        ),
        paymentTypeString: financial.paymentType,
        paymentPaidDate: financial.paidTimestamp
          ? moment(financial.paidTimestamp).format('DD/MM/YYYY HH:mm')
          : null,
        startDate: financial.startTimestamp
          ? moment(financial.startTimestamp).format('DD/MM/YYYY HH:mm')
          : null,
        paymentTransactionAmountHumanReadable:
          financial.paymentTransactionAmountRefunded
            ? mascaraMoeda(
                (
                  financial.paymentTransactionAmount -
                  financial.paymentTransactionAmountRefunded
                ).toFixed(2),
              )
            : financial.paymentTransactionAmountHumanReadable,
        externalPaymentReference: financial.paymentExternalId,
        value: financial.paymentTransactionAmountRefunded
          ? Number(
              (
                financial.paymentTransactionAmount -
                financial.paymentTransactionAmountRefunded
              ).toFixed(2),
            )
          : financial.paymentTransactionAmount,
        tenant: financial?.tenantName?.toLowerCase(),
        alias: financial.tenantAlias,
        chargeBox: financial.chargeBoxDescription,
        paidTimestamp: moment(
          financial.paidTimestamp || financial.startTimestamp,
        ).format('DD/MM/YYYY - HH:mm'),
        nfeStatus: financial.nfeStatus,
        energyCostHumanReadable: financial.energyCostHumanReadable,
        taxIncomeHumanReadable: financial.taxIncomeHumanReadable,
        moneyVoucherHumanReadable: financial.moneyVoucherHumanReadable,
        moneyCoin: financial.moneyCoin,
        moneyPerTransactionIncomeHumanReadable:
          financial.moneyPerTransactionIncomeHumanReadable,
        energyHumanReadable: financial.energyHumanReadable,
        moneyPerKilowattIncome: financial.moneyPerKilowattIncome,
        paymentTransactionAmountRefunded:
          financial.paymentTransactionAmountRefunded,
        paymentTransactionAmountAvailableHumanReadable:
          financial.paymentTransactionAmountAvailableHumanReadable,
        total: financial.paymentTransactionAmountHumanReadable,
        nfeUrls: resolveNfeUrls(financial.nfeDanfseUrls, financial.nfeUrls),
        splits: financial.splits ?? [],
      };
    };

    if (
      tabType === TabType.ALLMOVEMENTS ||
      tabType === TabType.RECHARGES ||
      tabType === TabType.DEPOSITS
    ) {
      const recharges = financialInfoTransactions?.data || [];
      dataSource.push(...recharges.map(mapFinancialAllMovements));
    }

    return dataSource;
  }

  return {
    financialInfoTransactions,
    financialInfoTransactionsSummary,
    openDrawerProps,
    tryToGetTransactions,
    tryToGetFinancialInfoSummary,
    generateDataSource,
    generateColumns,
    setOpenDrawerProps,
    downloadTransactions,
    handleStatus,
    tenants,
    chargeboxes,
  };
};
